import {
  IUpdateDraftPayload,
  toAnswerPayload,
} from "../services/serviceRequestCreation";
import { IAnswerMap, IQuestion } from "../types";
import { useDraftIdentity } from "./useDraftIdentity";
import { useSaveScreen } from "./useServiceRequestDraft";

/**
 * What a step screen submits: its form values keyed by question key, plus any
 * draft field that screen owns directly (the address screen writes `location`
 * and friends, the visibility screen writes `pro_only`).
 *
 * `questions` are the screen's own config questions, needed only because the
 * answers payload matches on `question_id` and an answer map holds keys — see
 * `toAnswerPayload`.
 *
 * `validate` asks the backend to screen the draft, and to publish it if it can
 * (a visitor's cannot until `claim`). Only the last config screen sends it —
 * see `updateServiceRequestDraft`.
 */
export interface ISubmitScreenInput
  extends Omit<IUpdateDraftPayload, "answers"> {
  answers?: IAnswerMap;
  questions?: IQuestion[];
  validate?: boolean;
}

const toPayload = ({
  answers,
  questions,
  validate,
  ...fields
}: ISubmitScreenInput): IUpdateDraftPayload & { validate?: boolean } => ({
  ...fields,
  ...(validate ? { validate } : {}),
  ...(answers ? { answers: toAnswerPayload(answers, questions ?? []) } : {}),
});

/**
 * The one hook a step screen needs to save itself.
 *
 * It knows which request is being saved (`useDraftIdentity`: the task when
 * editing, the stored draft token otherwise, per settled decision 6) and the
 * answers-array shape, so a screen hands over its `useForm` values and its
 * questions and nothing else. One PUT per screen, response written into the draft
 * cache — all of that is `useSaveScreen`'s job underneath, and none of it is a
 * screen's business.
 *
 * ```tsx
 * const { submit, isLoading, error } = useSubmitScreenAnswers();
 * const onSubmit = handleSubmit((values) =>
 *   submit({ answers: values, questions: screen.questions })
 * );
 * ```
 *
 * `submit` rejects when there is no draft — a screen reached without one has
 * nothing to save, and the navigation guard should have sent it to the
 * description screen.
 */
export const useSubmitScreenAnswers = () => {
  const identity = useDraftIdentity();
  const mutation = useSaveScreen(identity);

  const submit = (input: ISubmitScreenInput) =>
    identity.draftKey
      ? mutation.mutateAsync(toPayload(input))
      : Promise.reject(new Error("No service request draft to save"));

  return { ...mutation, submit, hasDraft: Boolean(identity.draftKey) };
};
